import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Col, Label, Row} from 'reactstrap';
import {AvCheckbox, AvCheckboxGroup, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import {getSymptomsByEmergency} from 'app/entities/symptom/symptom.reducer';
import 'app/entities/registration/registration.scss';
import {EmergencyType} from 'app/shared/model/enumerations/emergency-type.model';
import RegistrationButtons from './registration-buttons';

interface IBabySymptomsProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  onFinish?: () => void;
  updating?: boolean;
}

const BabySymptoms: React.FC<IBabySymptomsProps> = (props: IBabySymptomsProps) => {
  const {countryId, symptoms, loading, patientCase, registrationData} = props;
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [babySymptom, setBabySymptom] = useState(patientCase?.babySymptom || '');
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  useEffect(() => {
    if (countryId) {
      props.getSymptomsByEmergency(EmergencyType.NEONATAL, countryId);
      setSelectedSymptoms(registrationData.babySymptomsIds || []);
    }

  }, [countryId]);

  useEffect(() => {
    props.setRegistrationData({babySymptomsIds: selectedSymptoms});
  }, [selectedSymptoms]);

  useEffect(() => {
    // Update isOtherSelected when symptoms or selectedSymptoms change
    if (symptoms.length > 0 && selectedSymptoms.length > 0) {
      const sortedSymptoms = [...symptoms].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      const lastSymptom = sortedSymptoms[sortedSymptoms.length - 1];
      setIsOtherSelected(selectedSymptoms.includes(lastSymptom?.id));
    } else {
      setIsOtherSelected(false);
    }
  }, [symptoms, selectedSymptoms]);

  const handleSymptomChange = event => {
    // Sort symptoms by displayOrder and check if the last symptom (highest displayOrder) is selected
    const sortedSymptoms = [...symptoms].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const lastSymptom = sortedSymptoms[sortedSymptoms.length - 1];
    setIsOtherSelected(selectedSymptoms.includes(lastSymptom?.id));
  };

  const handleBabySymptomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setBabySymptom(value);
    props.setRegistrationData({patientCase: {...patientCase, babySymptom: value}});
  };

  return (
    <div>
      <h2 className="mb-4">Does the baby show any signs of the following symptoms?</h2>
      <h5 className="text-primary mb-4 text-uppercase">NEONATAL</h5>
      <AvForm onValidSubmit={props.onFinish}>
        <Row>
          <Col md="12">
            <AvGroup>
              <AvCheckboxGroup
                name="babySymptoms"
                className="form-control"
                style={{overflowY: 'auto', height: '400px'}}
                value={selectedSymptoms}
              >
                {loading ? (
                  <p>Loading symptoms...</p>
                ) : (
                  [...symptoms]
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map(symptom => (
                      <AvCheckbox
                        key={symptom.id}
                        value={symptom.id}
                        label={symptom.enCondition}
                        onChange={handleSymptomChange}
                      />
                    ))
                )}
              </AvCheckboxGroup>
            </AvGroup>
          </Col>
        </Row>
        {isOtherSelected && (
          <Row>
            <Col md="12">
              <AvGroup>
                <Label for="babySymptom">Clinical Details</Label>
                <AvInput
                  type="textarea"
                  name="babySymptom"
                  id="babySymptom"
                  value={babySymptom}
                  onChange={handleBabySymptomChange}
                  rows={5}
                  required
                />
              </AvGroup>
            </Col>
          </Row>
        )}
        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
          updating={props.updating}
          isLastStep={true}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  countryId: state.registration.entity.countryId,
  symptoms: state.symptom.entities,
  loading: state.symptom.loading,
  patientCase: state.registration.entity.patientCase,
  registrationData: state.registration.entity,
});

const mapDispatchToProps = {
  setRegistrationData,
  getSymptomsByEmergency,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(BabySymptoms);
